import { CommonModule } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { GroupedNotification, NotificationControllerService, UserData } from '@chicilon/angular-notification-service';
import { NotificationType, NotificationModule } from '@core/enum/notification';
import { APP_PERMISSION } from '@core/enum/permisson';
import { ReportService } from '@core/services/report.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomAvatarComponent } from '@shared/components/custom-avatar/custom-avatar.component';
import { TOAST_KEYS } from '@shared/constants/toast-keys';
import { TimeAgoPipe } from '@shared/pipes/time-ago.pipe';
import { endOfDay, startOfDay } from 'date-fns';
import { NgxPermissionsService } from 'ngx-permissions';
import { MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { BadgeModule } from 'primeng/badge';
import { DialogModule } from 'primeng/dialog';
import { Popover, PopoverModule } from 'primeng/popover';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { Tooltip } from 'primeng/tooltip';
import { Subject, takeUntil, interval, Subscription } from 'rxjs';

export interface NotificationItem {
  id?: string;
  module_type?: string;
  notification_type?: string;
  contract_id?: string;
  appendix_id?: string;
  booking_proposal_id?: string;
  booking_id?: string;
  booking_type_name?: string;
  tab?: string;
  to_user?: UserData;
  sender_user?: UserData;
  customer_id?: string;
  exploit_id?: string;
  content?: string;
  send_time?: string | Date;
  is_read?: boolean;
  booking_long_id?: number;
  booking_week_id?: string;
  change_date?: number;
  layout_request_id?: string;
}

@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [
    CommonModule,
    BadgeModule,
    ScrollPanelModule,
    TranslateModule,
    DialogModule,
    AvatarModule,
    PopoverModule,
    TimeAgoPipe,
    CustomAvatarComponent,
    Tooltip
  ],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss'
})
export class NotificationComponent implements OnInit, OnDestroy {
  @ViewChild('popover') popover!: Popover;
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.popover && this.popover.overlayVisible) {
      this.popover.hide();
    }
  }

  private timerSub?: Subscription;
  private unsubscribe$ = new Subject<void>();

  readonly TRANSLATE_PREFIX = 'NOTIFICATION.';
  readonly PAGE_SIZE = 20;
  private readonly SCROLL_THRESHOLD = 80;
  private justInitialized = true;

  notifications: GroupedNotification[] = [];
  visible = false;
  isReadAll = false;

  pageIndex = 0;
  loadedCount = 0;
  totalElement = 0;
  isLoadingMore = false;
  hasMore = true;

  canCreateReport = false;
  canEditReport = false;

  constructor(
    private router: Router,
    private notificationService: NotificationControllerService,
    private messageService: MessageService,
    private permissionsService: NgxPermissionsService,
    private sanitizer: DomSanitizer,
    private httpReportService: ReportService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.fetchNotifications(true);
    this.permissionsService.hasPermission(APP_PERMISSION.D_C).then(has => (this.canCreateReport = !!has));
    this.permissionsService.hasPermission(APP_PERMISSION.D_U).then(has => (this.canEditReport = !!has));
    this.timerSub = interval(60000).subscribe(() => this.cdr.markForCheck());
  }

  sanitizeContent(content: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(content);
  }

  fetchNotifications(reset = false) {
    if (this.isLoadingMore) return;

    if (reset) {
      this.pageIndex = 0;
      this.loadedCount = 0;
      this.totalElement = 0;
      this.hasMore = true;
      this.notifications = [];
    }

    if (!this.hasMore) return;

    this.isLoadingMore = true;
    const page = this.pageIndex;

    this.notificationService
      .getMyNotification(undefined, undefined, page, this.PAGE_SIZE, '-send_time')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          this.isLoadingMore = false;
          if (res.success) {
            const item = res?.data?.data?.[0];
            this.isReadAll = item?.is_read_all ?? false;
            const newGroups = item?.groups ?? [];

            this.notifications = reset ? newGroups : this.mergeGroups(this.notifications, newGroups);

            const fetchedCount = newGroups.reduce((sum, g) => sum + (g.notifications?.length ?? 0), 0);
            this.loadedCount += fetchedCount;
            this.totalElement = res?.data?.total_element ?? this.totalElement;
            this.hasMore = this.loadedCount < this.totalElement;

            if (this.hasMore) {
              this.pageIndex++;
            }
          }
        },
        error: err => {
          this.isLoadingMore = false;
          this.messageService.add({
            key: TOAST_KEYS.G_MESSAGE,
            severity: 'error',
            detail: err?.error?.error?.message || this.translateService.instant(this.TRANSLATE_PREFIX + 'FETCH-ERROR')
          });
        }
      });
  }

  private mergeGroups(existing: GroupedNotification[], incoming: GroupedNotification[]): GroupedNotification[] {
    if (incoming.length === 0) return existing;

    const result = [...existing];
    const lastGroup = result[result.length - 1];
    const firstIncoming = incoming[0];

    if (lastGroup && firstIncoming && lastGroup.date === firstIncoming.date) {
      result[result.length - 1] = {
        ...lastGroup,
        notifications: [...(lastGroup.notifications ?? []), ...(firstIncoming.notifications ?? [])]
      };
      return [...result, ...incoming.slice(1)];
    }

    return [...result, ...incoming];
  }

  onNotificationScroll(event: Event) {
    const target = event.target as HTMLElement;
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - this.SCROLL_THRESHOLD;

    if (reachedBottom && !this.isLoadingMore && this.hasMore) {
      this.fetchNotifications(false);
    }
  }

  onPopoverShow() {
    if (this.justInitialized) {
      this.justInitialized = false;
      return;
    }
    this.fetchNotifications(true);
  }

  onMaskAllAsRead() {
    this.notificationService
      .markAllAsRead()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.fetchNotifications(true);
        }
      });
  }

  isToday(dateStr: string): boolean {
    const today = new Date();
    const date = new Date(dateStr);
    return (
      today.getFullYear() === date.getFullYear() &&
      today.getMonth() === date.getMonth() &&
      today.getDate() === date.getDate()
    );
  }

  onReadNotification(id: string) {
    this.notificationService
      .readNotification(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.fetchNotifications(true);
        }
      });
  }

  onNavigateToCustomerDetails(customerId: string, saleId?: string, type?: string) {
    if (!customerId) return;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/crm/customer-management', customerId], {
        queryParams: type === 'PUBLIC' ? { saleId } : {}
      });
    });

    this.popover.hide();
  }

  onPeriodicEmailCheck(saleId?: string) {
    if (!saleId) return;

    const todayStart = startOfDay(new Date()).getTime();
    const todayEnd = endOfDay(new Date()).getTime();

    const paramReport = `sale_id==${saleId},created_at>=${todayStart},created_at<=${todayEnd}`;

    this.httpReportService.getPageDailyReport(paramReport, '-created_at', 0, 5).subscribe({
      next: res => {
        if (res.success) {
          const totalReports = res.data?.total_element || 0;
          if (totalReports === 0) {
            if (this.canCreateReport) {
              this.router.navigate(['/report/my-report/create']);
            } else {
              this.messageService.add({
                key: TOAST_KEYS.G_MESSAGE,
                severity: 'error',
                detail: this.translateService.instant('REPORT.REPORT_NO_CREATE_PERMISSION')
              });
            }

            this.popover.hide();
          } else if (totalReports === 1) {
            if (this.canEditReport) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const id = (res?.data?.data as any[])?.[0]?.id || '';

              this.router.navigate([`/report/my-report/edit/${id}`]);
            } else {
              this.messageService.add({
                key: TOAST_KEYS.G_MESSAGE,
                severity: 'error',
                detail: this.translateService.instant('REPORT.REPORT_NO_EDIT_PERMISSION')
              });
            }

            this.popover.hide();
          }
        }
      },
      error: err => {
        this.messageService.add({
          key: TOAST_KEYS.G_MESSAGE,
          severity: 'error',
          detail: err?.error?.error?.message
        });
      }
    });
  }

  onNavigateToReportPage(sendTime?: string | Date, staffId?: string,subType?:string) {
    const reportDate = sendTime ? new Date(sendTime) : new Date();
    const path = subType === NotificationType.OPERATION_REPORT ? '/exploit' : '';

    this.navigateIfAnyPermission(
      [APP_PERMISSION.DRE_MV, APP_PERMISSION.D_VA],
      `/report/report-management${path}`,
      {
        fromManagement: true,
        dateFrom: startOfDay(reportDate).getTime(),
        dateTo: endOfDay(reportDate).getTime(),
        staffIds: staffId || undefined
      }
    );
  }

  onNavigateToAppointmentPage() {
    this.navigateIfAnyPermission([APP_PERMISSION.AM_MV, APP_PERMISSION.C_VA], '/appointment/appointment-management');
  }

  private navigateIfAnyPermission(
    permissions: APP_PERMISSION[],
    path: string,
    queryParams?: Record<string, string | number | boolean | undefined>
  ) {
    Promise.all(permissions.map(p => this.permissionsService.hasPermission(p))).then(results => {
      const hasAccess = results.some(has => !!has);

      if (hasAccess) {
        this.router.navigate([path], { queryParams });
      } else {
        this.messageService.add({
          key: TOAST_KEYS.G_MESSAGE,
          severity: 'error',
          detail: this.translateService.instant('COMMON.NO_PERMISSION')
        });
      }

      this.popover.hide();
    });
  }

  onNavigate(
    notificationType: string,
    id: string,
    toUser?: UserData,
    sender_user?: UserData,
    subType?: string,
    customerId?: string,
    exploitId?: string,
    sendTime?: string | Date
  ) {
    this.onReadNotification(id);
    switch (notificationType) {
      case 'EXPLOIT':
        this.handleExploitNotification(subType, exploitId);
        break;
      case 'CRM':
        if (subType !== NotificationType.GDKD_REJECTED) {
          this.onNavigateToCustomerDetails(customerId || '', sender_user?.id, subType);
        }
        break;
      case 'HRM':
        if (subType === NotificationType.REPORT || subType === NotificationType.OPERATION_REPORT) {
          this.onNavigateToReportPage(sendTime, sender_user?.id,subType);
        } else if (subType === NotificationType.APPOINTMENT) {
          this.onNavigateToAppointmentPage();
        }
        break;
      case 'BOOKING':
        if (
          subType === NotificationType.START_DATE ||
          subType === NotificationType.END_DATE ||
          subType === NotificationType.MID_DATE
        ) {
          this.onPeriodicEmailCheck(toUser?.id);
        }
        break;
    }
  }

  getPlainText(html: string): string {
    if (!html) return '';

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const text = doc.body.textContent || '';
    return text.replace(/\s+/g, ' ').trim();
  }

  getSendTime(send_time: string | Date | null | undefined): string {
    if (!send_time) return '';

    const date = new Date(send_time);

    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  }

  async onNavigateV2(subNotification: NotificationItem) {
    const id = subNotification?.id;
    if (!id) return;
    this.onReadNotification(id);

    const moduleType = subNotification?.module_type;
    const notificationType = subNotification?.notification_type;
    const contractId = subNotification?.contract_id;
    const appendixId = subNotification?.appendix_id;

    let target: string | null = null;

    if (moduleType === 'BOOKING_WEEK') {
      const bookingProposalId = subNotification?.booking_proposal_id;
      const bookingId = subNotification?.booking_id;
      const bookingTypeName = subNotification?.booking_type_name;
      const tab = subNotification?.tab || 'booking_week';

      if (bookingProposalId && bookingId && bookingTypeName) {
        if (bookingTypeName === 'DURATION') {
          target = `/bookings/suggestion/view/${bookingProposalId}/duration/${bookingId}?tab=${tab}`;
        } else if (bookingTypeName === 'QUANTITY') {
          target = `/bookings/suggestion/${bookingProposalId}/quantity/view/${bookingId}?tab=${tab}`;
        }
      }
    } else if (moduleType === 'BOOKING_LAYOUT_REQUEST') {
      const bookingId = subNotification?.booking_id;
      const bookingLongId = subNotification?.booking_long_id;
      const bookingWeekId = subNotification?.booking_week_id;
      const bookingTypeName = subNotification?.booking_type_name;
      const changeDate = subNotification?.change_date;
      const layoutRequestId = subNotification?.layout_request_id;
      const layoutParam = layoutRequestId ? `&layout_request_id=${layoutRequestId}` : '';

      if (bookingTypeName === 'DURATION') {
        target = `/bookings/duration/layout/management/layout/add?booking_id=${bookingId}&booking_long_id=${bookingLongId}&booking_week_id=${bookingWeekId}&change_date=${changeDate}${layoutParam}`;
      } else if (bookingTypeName === 'QUANTITY') {
        target = `/bookings/quantity/layout/management/layout/add?booking_id=${bookingId}&booking_long_id=${bookingLongId}&booking_week_id=${bookingWeekId}&change_date=${changeDate}${layoutParam}`;
      }
    } else if (moduleType === NotificationModule.BOOKING) {
      target = contractId ? `/bookings/suggestion/view/${contractId}` : null;
    } else if (moduleType === NotificationModule.CONTRACT) {
      switch (notificationType) {
        case NotificationType.CONTRACT_OVERVIEW:
          target = contractId ? `/contracts/details/${contractId}/overview` : null;
          break;
        case NotificationType.APPENDIX_OVERVIEW:
          target = contractId && appendixId ? `/contracts/details/${contractId}/appendix/${appendixId}` : null;
          break;
        case NotificationType.ACCEPTANCE_OVERVIEW:
          target = contractId ? `/contracts/details/${contractId}/acceptance` : null;
          break;
        case NotificationType.EXTENSION_OVERVIEW:
          target = contractId ? `/contracts/details/${contractId}/extension` : null;
          break;
        case NotificationType.ADS_PLAN_OVERVIEW:
          target = contractId ? `/contracts/details/${contractId}/plan` : null;
          break;

        default:
          target = null;
          break;
      }
    }

    if (!target) return;
    await this.router.navigateByUrl('/', { skipLocationChange: true });
    if (moduleType === 'BOOKING_WEEK' || moduleType === 'BOOKING_LAYOUT_REQUEST' || moduleType === NotificationModule.BOOKING) {
      await this.router.navigateByUrl(target);
    } else {
      await this.router.navigate([target]);
    }
    this.popover?.hide();
  }

  handleExploitNotification(subType?: string, id?: string) {
    if (id && subType) {
      switch (subType) {
        case NotificationType.BUILDING:
          this.router.navigate([`/buildings/management/view/${id}/overview`]);
          break;
        case NotificationType.PROPOSAL:
          this.router.navigate([`/exploit/proposal/view/${id}/overview`]);
          break;
        case NotificationType.PAYMENT:
        case NotificationType.LIQUIDATION:
        case NotificationType.CONTRACT:
          this.router.navigate([`/exploit/contract/view/${id}/overview`]);
          break;
        default:
          break;
      }
    }
  }

  ngOnDestroy(): void {
    this.timerSub?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}